import React, { useState, useMemo } from 'react';
import { CARDS, isDivineCard, cardRarity } from '../game/cards';
import { Camera, Copy, Check, Share2, Sparkles } from 'lucide-react';

/**
 * v3.0 POST-RUN HIGHLIGHT REEL
 * Compact shareable stat card that summarizes the run's biggest moments.
 * Auto-generates a tweet-length copy string and lets users copy it to clipboard.
 */
export default function HighlightReel({ summary }) {
  const [copied, setCopied] = useState(false);
  const [copiedText, setCopiedText] = useState(false);

  const stats = useMemo(() => {
    if (!summary) return null;
    const history = summary.history || [];
    // biggest hit / longest combat / total dmg
    let biggestHit = 0;
    let totalDmgDealt = 0;
    let totalDmgTaken = 0;
    let longestCombat = 0;
    const bossKills = [];
    const eliteKills = [];
    for (const h of history) {
      if (h.biggestHit > biggestHit) biggestHit = h.biggestHit;
      totalDmgTaken += h.dmgTaken || 0;
      if ((h.turns || 0) > longestCombat) longestCombat = h.turns;
      if (h.isBoss && h.victory) bossKills.push(h);
      if (h.kind === 'elite' && h.victory) eliteKills.push(h);
    }
    // divine drops in final deck
    const deck = summary.player?.deck || [];
    const divines = deck.filter(id => isDivineCard(id));
    // highest rarity cards
    const legendaries = deck.filter(id => cardRarity(id) === 'legendary').length;
    const rares = deck.filter(id => cardRarity(id) === 'rare').length;
    return {
      biggestHit, totalDmgTaken, longestCombat,
      bossKills: bossKills.length,
      eliteKills: eliteKills.length,
      divines,
      legendaries, rares,
      deckSize: deck.length,
      relicCount: (summary.player?.relics || []).length,
    };
  }, [summary]);

  if (!summary || !stats) return null;

  const { victory, floor, score, seed, player, streak, ascension } = summary;
  const classIcon = player?.classId === 'rizzler' ? '💋' : player?.classId === 'sigma' ? '🏋' : player?.classId === 'gooner' ? '🛡' : '❓';

  const shareText =
    (victory ? '💀 W. GG. ' : '💀 DIED. ') +
    `${classIcon} FLOOR ${floor} · ${score} pts` +
    (ascension ? ` · A${ascension}` : '') +
    (streak > 0 ? ` · 🔥×${streak}` : '') +
    '\n' +
    `⚔️ BIGGEST HIT: ${stats.biggestHit} ` +
    `· 💀 DMG TAKEN: ${stats.totalDmgTaken} ` +
    (stats.divines.length > 0 ? `· ✦ DIVINE: ${stats.divines.length}` : '') +
    '\n' +
    (stats.bossKills > 0 ? `👑 ${stats.bossKills} boss${stats.bossKills > 1 ? 'es' : ''} slain · ` : '') +
    (stats.eliteKills > 0 ? `⚡ ${stats.eliteKills} elite${stats.eliteKills > 1 ? 's' : ''} · ` : '') +
    `🃏 ${stats.deckSize} cards\n` +
    `SEED: ${seed}  ·  play @ backendbrainrot.com`;

  const copySeed = () => {
    navigator.clipboard?.writeText(seed).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };
  const copyShare = () => {
    navigator.clipboard?.writeText(shareText).then(() => {
      setCopiedText(true); setTimeout(() => setCopiedText(false), 1800);
    });
  };
  const tweet = () => {
    const url = 'https://twitter.com/intent/tweet?text=' + encodeURIComponent(shareText);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="mt-10 w-full max-w-3xl mx-auto" data-testid="highlight-reel">
      <div
        className="brutal-border-pink p-6 bg-gradient-to-br from-[#0a0510] to-[#100a05]"
        style={{ borderColor: 'var(--pink)', boxShadow: '6px 6px 0 var(--pink), 0 0 30px rgba(255,0,127,0.25)' }}
      >
        <div className="flex items-center gap-2 mb-4">
          <Camera size={16} className="text-[color:var(--pink)]" />
          <div className="font-display font-black uppercase tracking-widest text-[color:var(--pink)] text-sm">
            HIGHLIGHT REEL · shareable stat card
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ReelStat label="BIGGEST HIT" value={stats.biggestHit} color="var(--red)" testid="reel-biggest-hit" />
          <ReelStat label="DMG TAKEN"   value={stats.totalDmgTaken} color="var(--pink)" testid="reel-dmg-taken" />
          <ReelStat label="LONGEST FIGHT" value={`${stats.longestCombat}T`} color="var(--neon)" testid="reel-longest-fight" />
          <ReelStat label="DIVINES"     value={stats.divines.length} color="#fff2a8" testid="reel-divines"
                    accent={stats.divines.length > 0 ? '✦' : null} />
          <ReelStat label="BOSSES"      value={stats.bossKills} color="var(--pink)" testid="reel-bosses" />
          <ReelStat label="ELITES"      value={stats.eliteKills} color="var(--neon)" testid="reel-elites" />
          <ReelStat label="LEGENDARIES" value={stats.legendaries} color="#ff2ea6" testid="reel-legendaries" />
          <ReelStat label="RELICS"      value={stats.relicCount} color="#ffd700" testid="reel-relics" />
        </div>

        {stats.divines.length > 0 && (
          <div className="mt-4 p-3 border-2 border-[#fff2a8] bg-[#1a1400]" data-testid="reel-divines-panel">
            <div className="text-[10px] uppercase tracking-widest text-[#fff2a8] flex items-center gap-2 mb-2">
              <Sparkles size={12}/> DIVINE CARDS COLLECTED
            </div>
            <div className="flex flex-wrap gap-2">
              {stats.divines.map((id, i) => {
                const c = CARDS[id];
                if (!c) return null;
                return (
                  <div key={i} className="flex items-center gap-1 px-2 py-1 border border-[#fff2a8] bg-black" data-testid={`reel-divine-${id}`}>
                    <span className="text-lg">{c.art}</span>
                    <span className="text-[10px] font-mono uppercase text-white font-bold">{c.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-4 border-2 border-[color:var(--text-dim)] bg-black p-3 font-mono text-[11px] whitespace-pre-wrap text-[color:var(--text)]" data-testid="reel-share-preview">
          {shareText}
        </div>

        <div className="mt-4 flex gap-2 flex-wrap">
          <button
            className="brutal-btn text-xs px-3 py-2"
            onClick={copyShare}
            data-testid="reel-copy-text-button"
          >
            {copiedText ? <Check size={14}/> : <Copy size={14}/>} {copiedText ? 'COPIED!' : 'COPY SHARE TEXT'}
          </button>
          <button
            className="brutal-btn text-xs px-3 py-2"
            onClick={copySeed}
            data-testid="reel-copy-seed-button"
          >
            {copied ? <Check size={14}/> : <Copy size={14}/>} SEED: {seed}
          </button>
          <button
            className="brutal-btn-pink brutal-btn text-xs px-3 py-2"
            onClick={tweet}
            data-testid="reel-tweet-button"
          >
            <Share2 size={14}/> TWEET
          </button>
        </div>
      </div>
    </div>
  );
}

function ReelStat({ label, value, color, testid, accent }) {
  return (
    <div className="brutal-border-dim p-3 bg-[#0a0a0a]" style={{ borderColor: color }}>
      <div className="text-[9px] uppercase tracking-widest text-[color:var(--text-dim)]">{label}</div>
      <div className="font-display text-2xl font-black mt-1 flex items-center gap-1" style={{ color }} data-testid={testid}>
        {accent && <span>{accent}</span>}
        {value}
      </div>
    </div>
  );
}
